import { callParty, outcomeChipTone } from "@phone/callParty";
import { callOutcome } from "@phone/phoneCallOutcome";
import type { PhoneCall } from "@phone/phoneCallsClient";
import {
  Button,
  Chip,
  ClockIcon,
  cn,
  EmptyNote,
  formatClockTime,
  HistoryRail,
} from "@scribenote/brian-design-system";
import { memo } from "react";

const dotFill = {
  recorded: "border-ink-recorded bg-fill-recorded",
  brian: "border-ink-brian bg-fill-brian",
  attention: "border-ink-attention bg-fill-attention",
  declined: "border-ink-declined bg-fill-declined",
} as const;

const dayFormat = new Intl.DateTimeFormat("en", {
  month: "short",
  day: "numeric",
});

export const callWhen = (iso: string): string => {
  const at = new Date(iso);
  const clock = formatClockTime(at);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (at.toDateString() === today.toDateString()) return clock;
  if (at.toDateString() === yesterday.toDateString())
    return `Yesterday · ${clock}`;
  return `${dayFormat.format(at)} · ${clock}`;
};

export const CallLog = ({
  calls,
  selectedId,
  onSelect,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  calls: PhoneCall[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) => {
  if (calls.length === 0)
    return <EmptyNote className="mt-4">No calls yet.</EmptyNote>;

  return (
    <div className="flex flex-col gap-3 pt-3.5">
      <HistoryRail aria-label="Call log" className="ml-2">
        {calls.map((call, index) => (
          <CallLogRow
            key={call.id}
            call={call}
            index={index}
            selected={call.id === selectedId}
            onSelect={onSelect}
          />
        ))}
      </HistoryRail>
      {hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          className="self-center"
          data-testid="phone-load-more"
          busy={isFetchingNextPage}
          onClick={onLoadMore}
        >
          Older calls
        </Button>
      )}
    </div>
  );
};

/** Clinical text: out of Session Replay; testids use the index, never a record id. */
const CallLogRow = memo(function CallLogRow({
  call,
  index,
  selected,
  onSelect,
}: {
  call: PhoneCall;
  index: number;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const party = callParty(call);
  const outcome = callOutcome(call);
  const tone =
    outcome.tone === "neutral" ? null : outcomeChipTone[outcome.tone];

  return (
    <li
      aria-current={selected ? "true" : undefined}
      className={cn(
        "relative rounded-md border transition duration-150 ease-paper",
        selected ? "border-accent bg-tint-accent" : "border-transparent",
      )}
      data-testid={`phone-call-${index}`}
    >
      <span
        aria-hidden="true"
        className={cn(
          "absolute -left-[20.75px] top-4 size-2.5 rounded-full border-[1.5px]",
          selected
            ? "border-accent bg-accent"
            : tone
              ? dotFill[tone]
              : "border-hollow bg-card",
        )}
      />
      <button
        type="button"
        onClick={() => onSelect(call.id)}
        className={cn(
          "flex w-full cursor-pointer flex-col rounded-md px-3 py-2.5 text-left",
          "focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent",
          selected ? "hover:bg-transparent" : "hover:bg-smudge",
        )}
      >
        <span className="flex flex-wrap items-center justify-between gap-x-2.5 gap-y-1">
          <time
            dateTime={call.createdAt}
            className="inline-flex items-center whitespace-nowrap text-mono-time text-fine-print text-ink-body"
          >
            <ClockIcon className="mr-[0.35em]" />
            {callWhen(call.createdAt)}
          </time>
          {tone ? (
            <Chip tone={tone} size="sm">
              {outcome.label}
            </Chip>
          ) : (
            <span className="text-fine-print text-annotation">
              {outcome.label}
            </span>
          )}
        </span>
        <span className="mt-1 flex items-baseline justify-between gap-2">
          <span
            className="min-w-0 flex-1 truncate text-[0.9375rem] font-semibold text-ink"
            data-dd-privacy="hidden"
            data-testid={`phone-call-caller-${index}`}
          >
            {party.name}
          </span>
          <span className="shrink-0 text-fine-print text-annotation">
            {party.outbound ? "Outgoing" : "Incoming"}
          </span>
        </span>
      </button>
    </li>
  );
});
